import { z } from 'zod';

import { GIFT_CARD_THEME_KEYS } from '@/lib/gift-card-design';
import { CartData } from '@/lib/types';

const CART_KEY = 'cartData';

// Only the fields checkout dereferences without a guard. Mirroring the whole
// offering type would be a second copy that drifts, and rejecting a field the
// UI already falls back on would throw away a cart that renders fine.
const dateString = z
  .string()
  .refine((value) => !Number.isNaN(Date.parse(value)));

const ITEM_SCHEMA = {
  class: z.object({
    class_id: z.string().min(1),
    quantity: z.number(),
    start_time: dateString,
    end_time: dateString,
  }),
  'class-pack': z.object({
    class_pack_id: z.string().min(1),
    quantity: z.number(),
  }),
  subscription: z.object({
    subscription_id: z.string().min(1),
  }),
  'gift-card': z.object({
    gift_card_id: z.string().min(1),
    quantity: z.number(),
    look: z.discriminatedUnion('type', [
      z.object({
        type: z.literal('theme'),
        theme_key: z.enum(GIFT_CARD_THEME_KEYS),
      }),
      z.object({
        type: z.literal('image'),
        text_tone: z.enum(['LIGHT', 'DARK']),
      }),
    ]),
  }),
};

const cartSchema = z.discriminatedUnion('checkoutType', [
  z.object({
    checkoutType: z.literal('class'),
    totalAmount: z.number(),
    items: z.array(ITEM_SCHEMA.class).nonempty(),
  }),
  z.object({
    checkoutType: z.literal('class-pack'),
    totalAmount: z.number(),
    items: z.array(ITEM_SCHEMA['class-pack']).nonempty(),
  }),
  z.object({
    checkoutType: z.literal('subscription'),
    totalAmount: z.number(),
    items: z.array(ITEM_SCHEMA.subscription).nonempty(),
  }),
  z.object({
    checkoutType: z.literal('gift-card'),
    totalAmount: z.number(),
    items: z.array(ITEM_SCHEMA['gift-card']).nonempty(),
  }),
]);

export const parseCart = (raw: string): CartData | null => {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  return cartSchema.safeParse(value).success ? (value as CartData) : null;
};

export const readCart = (): CartData | null => {
  const raw = localStorage.getItem(CART_KEY);
  if (!raw) return null;

  const cart = parseCart(raw);
  // An unreadable value would throw on every future visit to checkout, so drop
  // it here rather than leave the page permanently stuck for this browser.
  if (!cart) localStorage.removeItem(CART_KEY);
  return cart;
};

export const writeCart = (cart: CartData) =>
  localStorage.setItem(CART_KEY, JSON.stringify(cart));

export const clearCart = () => localStorage.removeItem(CART_KEY);

// An embed hands its cart over in the URL fragment, which the browser never
// sends to a server — so it stays out of access logs, out of request-line
// limits, and out of the pathname + search that analytics record.
export const consumeCartFromUrl = () => {
  const raw = new URLSearchParams(window.location.hash.slice(1)).get('cart');
  if (!raw) return;

  const cart = parseCart(raw);
  if (cart) writeCart(cart);

  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search
  );
};
